import qs from "qs";
import * as React from "react";

const getParams = (uri: string) => {
    if (!uri) {
        return;
    }

    const params = qs.parse(uri, {
        ignoreQueryPrefix: true,
        strictNullHandling: true,
        decoder: value => {
            if (/^(\d+|\d*\.\d+)$/.test(value)) {
                return parseFloat(value);
            }

            const keywords = {
                true: true,
                false: false,
                null: null,
                undefined,
            };

            if (value in keywords) {
                return keywords[value];
            }

            return decodeURIComponent(value);
        },
    });

    return params;
};

type QueryParamFunction<T> = () => T;

type QueryParamsProp<T extends object> = T | QueryParamFunction<T>;

export const useQueryParams = <T extends object>(
    defaultParams?: QueryParamsProp<T>
) => {
    const defaultCriteria = React.useRef<T>();
    const _search = location.search;

    return React.useMemo(() => {
        const p = getParams(_search);
        let d = defaultCriteria.current;
        if (!d) {
            if (!!defaultParams) {
                if (defaultParams instanceof Function) {
                    d = defaultParams();
                } else {
                    d = defaultParams;
                }
            }
            defaultCriteria.current = d;
        }

        return { ...d, ...p };
        // we do NOT want to rerun this when defaultParams changes
        // we only accept the initial.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_search]);
};

export const useQueryParamsExecute = <T extends object>(
    callback: (params: T) => Promise<void> | void,
    defaultParams?: QueryParamsProp<T>
) => {
    const queryParams = useQueryParams(defaultParams);

    React.useEffect(() => {
        const fn = async () => {
            await callback(queryParams as T);
        };
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams]);
};
